/**
 * CounterpartySelector Component
 *
 * Autocomplete input for selecting or creating counterparties.
 * Shows existing counterparties as suggestions while typing,
 * allows creating new ones by just typing a new name.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { counterpartyService } from '../../services/counterparty.service';
import type { Counterparty } from '@budgira/shared';
import { cn } from '../../utils/cn';

interface CounterpartySelectorProps {
  value: string;
  onChange: (value: string, counterpartyId?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

export function CounterpartySelector({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  error,
}: CounterpartySelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [filteredCounterparties, setFilteredCounterparties] = useState<Counterparty[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load counterparties on mount
  useEffect(() => {
    const loadCounterparties = async () => {
      setIsLoading(true);
      try {
        const data = await counterpartyService.list();
        // Sort alphabetically
        data.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        setCounterparties(data);
      } catch (err) {
        console.error('Failed to load counterparties:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadCounterparties();
  }, []);

  // Filter counterparties based on input value
  useEffect(() => {
    if (!value.trim()) {
      setFilteredCounterparties(counterparties.slice(0, 10)); // Show first 10 when empty
    } else {
      const normalizedValue = value.toLowerCase().trim();
      const filtered = counterparties.filter(c =>
        c.name.toLowerCase().includes(normalizedValue)
      );
      setFilteredCounterparties(filtered.slice(0, 10)); // Limit to 10 results
    }
    setHighlightedIndex(-1);
  }, [value, counterparties]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Check if the typed value exactly matches an existing counterparty (case-insensitive)
    const matchingCounterparty = counterparties.find(
      c => c.name.toLowerCase() === newValue.toLowerCase().trim()
    );
    // If exact match found, use its ID; otherwise clear the ID (new counterparty will be created)
    onChange(newValue, matchingCounterparty?.id);
    setIsOpen(true);
  };

  const handleSelectCounterparty = useCallback((counterparty: Counterparty) => {
    onChange(counterparty.name, counterparty.id);
    setIsOpen(false);
    inputRef.current?.blur();
  }, [onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredCounterparties.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredCounterparties[highlightedIndex]) {
          handleSelectCounterparty(filteredCounterparties[highlightedIndex]);
        } else {
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  // Check if current value exactly matches an existing counterparty
  const exactMatch = counterparties.find(
    c => c.name.toLowerCase() === value.toLowerCase().trim()
  );
  const isNewCounterparty = value.trim() && !exactMatch;

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full px-4 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-colors',
          error
            ? 'border-red-500 dark:border-red-500'
            : 'border-slate-300 dark:border-slate-600',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        role="combobox"
      />

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
              {t('common.loading', 'Chargement...')}
            </div>
          ) : (
            <>
              {/* Show "new counterparty" hint if typing something not in list */}
              {isNewCounterparty && (
                <div className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <span className="inline-flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('operations.form.newCounterparty', 'Nouveau tiers : "{{name}}"', { name: value.trim() })}
                  </span>
                </div>
              )}

              {/* Counterparty list */}
              {filteredCounterparties.length > 0 ? (
                <ul
                  ref={listRef}
                  className="max-h-48 overflow-y-auto"
                  role="listbox"
                >
                  {filteredCounterparties.map((counterparty, index) => (
                    <li
                      key={counterparty.id}
                      onClick={() => handleSelectCounterparty(counterparty)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={cn(
                        'px-4 py-2 cursor-pointer text-sm',
                        highlightedIndex === index
                          ? 'bg-violet-100 dark:bg-violet-900/50 text-violet-900 dark:text-violet-100'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                      )}
                      role="option"
                      aria-selected={highlightedIndex === index}
                    >
                      {counterparty.name}
                    </li>
                  ))}
                </ul>
              ) : (
                !isNewCounterparty && (
                  <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                    {t('operations.form.noCounterpartyFound', 'Aucun tiers trouvé')}
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CounterpartySelector;
